import { CONSENT_SHEET, CONSENT_VERSION } from '@aera/content/client';
import type { ClientConfig, ConsentStep, Lang } from '@aera/schema';
import { tidy } from '@aera/ui';
import { Mic } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '../components/ui.js';
import { Footer, Page } from './Chrome.js';
import { ConsentCheckbox } from './questions/CheckboxQuestion.js';
import { CrossBorderText, ProcessingText, consentVars, versionLine } from './ConsentText.js';

/**
 * The button that led here ("I don't agree", "I've changed my mind", Agree) went with the screen it was on,
 * leaving focus on <body>; the new screen's heading takes it, so a screen reader says where they are now.
 * Not on the page's first screen (`moved` false, e.g. a declined link reopened): focus is on <body> there
 * because nothing has been focused yet, and a focus from script before any key or tap draws the focus ring.
 */
function useHeadingFocus(moved: boolean) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (moved && (!document.activeElement || document.activeElement === document.body)) heading.current?.focus();
    // On mount only: the screen that replaced another.
  }, []);
  return heading;
}

/**
 * The processing consent and the cross-border consent are two separate screens, each with its own text and
 * checkbox: one checkbox on a long policy was ruled insufficient for an overseas transfer (Guangzhou
 * Internet Court, 2024).
 */
export function ConsentScreen({
  kind,
  lang,
  config,
  guided,
  accepted,
  onAccept,
  onDecline,
  onBack,
  busy,
  moved,
}: {
  kind: ConsentStep;
  lang: Lang;
  config: ClientConfig;
  guided: boolean;
  accepted: boolean;
  onAccept: (textVersion: string) => void;
  onDecline?: (textVersion: string) => void;
  onBack: () => void;
  busy: boolean;
  /** This screen replaced another in the flow (not the first one shown on load). */
  moved: boolean;
}) {
  const { t } = useTranslation();
  const heading = useHeadingFocus(moved);
  const [checked, setChecked] = useState(accepted);
  useEffect(() => setChecked(accepted), [kind, accepted]);
  const vars = consentVars(config);
  const version = config.consentVersion || CONSENT_VERSION;
  const sheet = kind === 'processing' ? CONSENT_SHEET.processing : CONSENT_SHEET.crossBorder;
  // With the model on Aera's own server the tick must not agree to processing by a model abroad.
  const checkbox = kind === 'crossBorder' && config.llmMode === 'local' ? CONSENT_SHEET.crossBorder.checkboxLocalModel : sheet.checkbox;

  return (
    <>
      <Page className="print-break-avoid">
        <p className="text-[13px] text-ink-2">{t('consent.pageOf', { n: kind === 'processing' ? 1 : 2 })}</p>
        <h1 ref={heading} tabIndex={-1} className="mt-1 text-[24px] font-semibold leading-snug tracking-[-0.01em] outline-none">
          {tidy(sheet.title[lang])}
        </h1>
        {guided && (
          <p className="mt-3 flex items-start gap-2 rounded-2xl bg-surface px-4 py-3 text-[14px] text-ink-2">
            <Mic className="mt-0.5 h-4 w-4 shrink-0 text-indigo" aria-hidden />
            {t('consent.guidedNote')}
          </p>
        )}
        <div className="mt-5">
          {kind === 'processing' ? <ProcessingText lang={lang} vars={vars} /> : <CrossBorderText lang={lang} vars={vars} llmMode={config.llmMode} />}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-ink-2">
          <span>{versionLine(lang, version, vars)}</span>
          <button type="button" className="underline underline-offset-4 hover:text-black lp-noprint" onClick={() => window.print()}>
            {t('consent.saveThisPage')}
          </button>
          <a href="/privacy" target="_blank" rel="noopener" className="underline underline-offset-4 hover:text-black lp-noprint">
            {t('consent.fullText')}
          </a>
        </div>
        <div className="mt-6">
          <ConsentCheckbox id={`consent-${kind}`} label={checkbox[lang]} checked={checked} onChange={setChecked} />
        </div>
        {kind === 'crossBorder' && onDecline && (
          <div className="mt-5">
            <Button variant="ghost" size="sm" className="-ml-3" onClick={() => onDecline(version)} disabled={busy}>
              {t('consent.decline')}
            </Button>
            <p className="mt-1 text-[13px] text-ink-2">{CONSENT_SHEET.crossBorder.declineHint[lang]}</p>
          </div>
        )}
      </Page>
      <Footer onBack={onBack} onNext={() => onAccept(version)} nextDisabled={!checked} busy={busy} nextLabel={t('consent.agreeContinue')}>
        {!checked && <p className="text-center text-[12.5px] text-ink-2">{t('consent.mustAgree')}</p>}
      </Footer>
    </>
  );
}

export function DeclinedScreen({
  config,
  onChangeMind,
  moved,
}: {
  config: ClientConfig;
  onChangeMind: () => void;
  /** This screen replaced another in the flow (not the first one shown on load). */
  moved: boolean;
}) {
  const { t } = useTranslation();
  const heading = useHeadingFocus(moved);
  return (
    <Page className="flex min-h-[70svh] flex-col justify-center">
      <h1 ref={heading} tabIndex={-1} className="text-[26px] font-semibold leading-snug outline-none">
        {t('declined.title')}
      </h1>
      <p className="mt-4 text-ink-2">{t('declined.body')}</p>
      <div className="mt-8 flex flex-wrap gap-3">
        {config.bookingUrl && (
          <a href={config.bookingUrl} target="_blank" rel="noopener" className="inline-flex h-11 items-center rounded-full bg-black px-5 text-[15px] font-medium text-white">
            {t('declined.book')}
          </a>
        )}
        <Button variant="outline" onClick={onChangeMind}>
          {t('declined.changeMind')}
        </Button>
      </div>
      <p className="mt-6 text-[14px] text-ink-2">
        {/* Without a booking link this is the only way to ask for the call: one tap on a phone. */}
        <Trans
          i18nKey="declined.email"
          values={{ email: config.privacyEmail }}
          components={{ mail: <a href={`mailto:${config.privacyEmail}`} className="underline underline-offset-4 hover:text-black" /> }}
        />
      </p>
    </Page>
  );
}
